import { useMsal } from '@azure/msal-react'
import { PFlex } from '@porsche-design-system/components-react'
import React from 'react'
import { LightgalleryProvider } from 'react-lightgallery'
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom'
import { getCookie, setCookie } from 'react-use-cookie'

import Drawer from '../shared/components/Drawer/Drawer'
import Navigation from '../shared/components/Navigation/Navigation'
import { ApplicationContext } from '../shared/utils/Context/ApplicationContext'
import { acquireAccessToken, getUserRoleFromAccessToken } from '../shared/utils/GetAccess'
import ApplicationContent from './Applications/ApplicationsContent'
import HowTo, { PageContent } from './HowTo/HowTo'
import IntegrationsGuide from './IntegrationsGuide/IntegrationsGuide'
import MobileBlocker from './MobileBlocker/MobileBlocker'
import Reporting from './Reporting/Reporting'

import { PortalContext, UserRole } from '../shared/utils/PortalContext'

import _urlguide from '../shared/content/urlguide.json'
import _userguide from '../shared/content/userguide.json'
import 'lightgallery.js/dist/css/lightgallery.css'
import styles from './portal.module.css'

export default function Portal() {
  // Initiales Datum ist das aktuelle Quartal
  const today = new Date()
  const quarter = Math.floor(today.getMonth() / 3)
  const start = new Date(new Date(today.getFullYear(), quarter * 3, 1).setHours(0, 0, 0, 0))
  const end = new Date(new Date(start.getFullYear(), start.getMonth() + 3, 0).setHours(23, 59, 59, 999))
  const userguide = _userguide as PageContent
  const urlguide = _urlguide as PageContent

  const [openDrawer, setOpenDrawer] = React.useState(false)
  const [drawerContent, setDrawerContent] = React.useState('NewApp')
  const [toast, setToast] = React.useState(false)
  const [toastContent, setToastContent] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [loadingApps, setLoadingApps] = React.useState(true)
  const [drawerData, setDrawerData] = React.useState({})
  const [applications, setApplications] = React.useState([{}])
  const [searchValue, setSearchValue] = React.useState('')
  const { instance } = useMsal()
  const [accessToken, setAccessToken] = React.useState(getCookie('accessToken'))
  const [openModal, setOpenModal] = React.useState(false)
  const [openModalExport, setOpenModalExport] = React.useState(false)
  const [contentTitle, setContentTitle] = React.useState('')
  const [modalData, setModalData] = React.useState([])
  const [modalHeadlines, setModalHeadlines] = React.useState([])
  const [userRole, setUserRole] = React.useState(UserRole.ApplicationManager)
  const [error, setError] = React.useState(false)
  const EXPIRATION_FACTOR = 0.0209
  const routes = [
    {
      path: '/url-guide',
      main: <HowTo data={urlguide} />
    },
    {
      path: '/user-guide',
      main: <HowTo data={userguide} />
    },
    {
      path: '/api-guide',
      main: <IntegrationsGuide type='api' />
    },
    { path: '/', exact: true, main: <Reporting /> },
    {
      path: '/applications',
      main: <ApplicationContent />
    }
  ]

  const setNewTokenAsCookie = () => {
    acquireAccessToken(instance)
      .then((response) => {
        setCookie('accessToken', response, { days: EXPIRATION_FACTOR })
        setCookie('accessTokenExpiration', new Date(Date.now() + EXPIRATION_FACTOR * 864e5).toUTCString())
        setAccessToken(getCookie('accessToken'))
      })
      .catch((err) => {
        setError(true)
        setToastContent(err.message)
        setToast(true)
      })
  }

  React.useEffect(() => {
    let expireTime
    if (getCookie('accessToken') === '') {
      setNewTokenAsCookie()
    }
    if (getCookie('accessTokenExpiration') !== '') {
      const expirationDate = new Date(getCookie('accessTokenExpiration'))
      const now = new Date(Date.now())
      expireTime = expirationDate.getTime() - now.getTime()
    } else {
      expireTime = EXPIRATION_FACTOR * 864e5
    }
    setInterval(function () {
      setNewTokenAsCookie()
    }, expireTime)
  }, [])

  React.useEffect(() => {
    if (accessToken !== '' && error === false) {
      setUserRole(getUserRoleFromAccessToken(accessToken))
    }
    if (accessToken === 'not authorized') {
      setNewTokenAsCookie()
    }
  }, [accessToken])

  return (
    <>
      <MobileBlocker />
      <LightgalleryProvider>
        <PortalContext.Provider
          value={{
            openDrawer,
            setOpenDrawer,
            drawerContent,
            setDrawerContent,
            drawerData,
            setDrawerData,
            toast,
            setToast,
            toastContent,
            setToastContent,
            accessToken,
            setAccessToken,
            loading,
            setLoading,
            openModal,
            setOpenModal,
            openModalExport,
            setOpenModalExport,
            contentTitle,
            setContentTitle,
            modalData,
            setModalData,
            modalHeadlines,
            setModalHeadlines,
            userRole,
            setUserRole,
            error,
            setError
          }}
        >
          <ApplicationContext.Provider
            value={{ applications, setApplications, loadingApps, setLoadingApps, searchValue, setSearchValue }}
          >
            <PFlex className={styles.portal}>
              <Router>
                <Navigation />
                {accessToken !== '' && (
                  <Switch>
                    {routes.map((route, index) => (
                      <Route key={index} path={route.path} exact={route.exact} children={route.main} />
                    ))}
                  </Switch>
                )}
                <Drawer />
              </Router>
            </PFlex>
          </ApplicationContext.Provider>
        </PortalContext.Provider>
      </LightgalleryProvider>
    </>
  )
}
